/**
 * The drawer and its four levels: country profile, dimension detail, region
 * profile, methodology — plus assisted discovery.
 *
 * This is where the partial v1 delivery is most visible. A dimension with no
 * data draws a dashed axis labelled "not collected" instead of a zero vertex, the
 * dimension list shows an em dash, opening it lands on an empty state, and the
 * overall level is described as covering 5 of 6 dimensions.
 *
 * Panels drive the globe and the legend, never the other way round.
 */
import {
  DIMS, INDS, COVER, TIER_COLORS, TIER_LABEL, TIER_NAME, tierOf, catsByDim, hasDim,
  DIMS_ACTIVE, C, byName, EU_MEMBERS, fmt, fmtv, tierc, colorOf, META,
} from '../model.js';
import { gapsFor, holdersAreConcentrated } from '@atlas/shared';
import { highlights } from '../insights.js';
import { reduceMotion } from '../env.js';
import { mapView, repaint, setSelected, setDim } from '../globe/texture.js';
import { cam, flyToLL, buildBeacons } from '../globe/scene.js';
import { renderLegend, applyFilters } from './legend.js';
import { syncHash } from './hash.js';
import { h } from '../untrusted.js';

const drawer=document.getElementById('drawer'), dbody=document.getElementById('dbody'),
      dcrumb=document.getElementById('dcrumb'), dback=document.getElementById('dback');
let view=null;

/** What the drawer is showing, so the router can tell whether it already matches the URL. */
export const currentView = () => view;
const setView = v => { view = v; syncHash(v) };
dback.onclick=()=>{ if(view&&view.mode==='detail') openCountry(view.c,true) };
export function closeDrawer(){
  drawer.classList.remove('open'); document.body.classList.remove('drawer-open');
  setView(null); setSelected(0); applyFilters();   // restore any EU-highlight dimming
}
function openDrawer(){ drawer.classList.add('open'); document.body.classList.add('drawer-open'); dbody.scrollTop=0 }
function countUp(el,target){
  if(reduceMotion){ el.firstChild.textContent=fmt(target); return }
  const t0=performance.now();
  const step=now=>{ const k=Math.min(1,(now-t0)/800); el.firstChild.textContent=fmt(target*(1-Math.pow(1-k,3))); if(k<1) requestAnimationFrame(step) };
  requestAnimationFrame(step);
}
function radarSVG(c){
  const S=480, SH=330, cx=S/2, cy=SH/2+6, R=96, N=DIMS.length;
  const pt=(i,v,r=R)=>{ const a=-Math.PI/2+i*2*Math.PI/N; return [cx+Math.cos(a)*r*v/4, cy+Math.sin(a)*r*v/4] };
  const val=d=> c.dim[d]==null ? 0 : c.dim[d];   // an uncollected axis collapses to the centre
  const g=[];
  for(let ring=1;ring<=4;ring++) g.push(h`<circle class="ring" cx="${cx}" cy="${cy}" r="${R*ring/4}"/>`);
  g.push(DIMS.map((d,i)=>{ const [x,y]=pt(i,4);
    return h`<line class="axis${c.dim[d]==null?' axis-nd':''}" x1="${cx}" y1="${cy}" x2="${x}" y2="${y}"/>` }));
  g.push([2,4].map(r=>h`<text class="rlab" x="${cx+3}" y="${cy-R*r/4-3}">${r}</text>`));
  const poly=DIMS.map((d,i)=>pt(i,val(d)).join(',')).join(' ');
  const zero=DIMS.map(()=>cx+','+cy).join(' ');
  const labels=DIMS.map((d,i)=>{
    const a=-Math.PI/2+i*2*Math.PI/N;
    const lx=cx+Math.cos(a)*(R+30), ly=cy+Math.sin(a)*(R+24);
    const anchor=Math.abs(Math.cos(a))<.3?'middle':Math.cos(a)>0?'start':'end';
    return h`<text class="dlab${c.dim[d]==null?' dlab-nd':''}" data-dim="${d}" x="${lx}" y="${ly}" text-anchor="${anchor}">${d}</text>
      <text class="vlab" x="${lx}" y="${ly+17}" text-anchor="${anchor}">${c.dim[d]==null?'not collected':fmt(c.dim[d])}</text>`;
  });
  const vx=DIMS.map((d,i)=>{ if(c.dim[d]==null) return '';
    const [x,y]=pt(i,c.dim[d]); return h`<circle class="vx" data-dim="${d}" cx="${x}" cy="${y}" r="3.2"/>` });
  return h`<svg id="radar" style="max-width:100%" width="${S}" height="${SH}" viewBox="0 0 ${S} ${SH}">${g}<polygon class="poly" points="${reduceMotion?poly:zero}" data-target="${poly}"/>${vx}${labels}</svg>`;
}
function animateRadar(){
  const poly=dbody.querySelector('#radar .poly');
  if(!poly||reduceMotion) return;
  const target=poly.dataset.target.split(' ').map(p=>p.split(',').map(Number));
  const from=poly.getAttribute('points').split(' ').map(p=>p.split(',').map(Number));
  const t0=performance.now();
  const step=now=>{
    const k=Math.min(1,(now-t0)/700), e=1-Math.pow(1-k,3);
    poly.setAttribute('points', target.map((t,i)=>[from[i][0]+(t[0]-from[i][0])*e, from[i][1]+(t[1]-from[i][1])*e].join(',')).join(' '));
    if(k<1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
export function openCountry(c, back=false){
  if(!c) return;
  setView({mode:'country', c});
  dback.style.display='none';
  dcrumb.innerHTML=String(h`ATLAS <b>›</b> ${c.r.toUpperCase()} <b>›</b> ${c.n.toUpperCase()}`);
  const t=tierOf(c.overall);
  // no map geometry means a supranational entity. Several indicators simply do not
  // apply to one, and the delivery records those as 0, so its overall level would be
  // a claim the data cannot support. Show the profile, withhold the level.
  const isEU = !c.id;
  dbody.innerHTML=String(h`
    <section class="dsec">
      <div class="eyebrow">${isEU?'Supranational entity':c.r}</div>
      <h2 class="cname">${c.n}</h2>
      <div class="heroline" style="margin-top:14px">
        <div>
          ${isEU?'':h`<span class="tierchip"><span class="dot" style="background:${TIER_COLORS[t]}"></span>${TIER_NAME[t]} capability</span>`}
          <div class="subline">${isEU
            ? 'Member states are highlighted on the globe. Several indicators — launch sites, domestic launches, operational launchers — do not apply to a supranational entity and are recorded as 0, so no overall level is shown until a "not applicable" convention is agreed.'
            : 'Capability profile across '+DIMS_ACTIVE.length+' of '+DIMS.length+' dimensions'}</div>
        </div>
      </div>
    </section>
    <section class="dsec">
      <div class="sechead">Dimension signature</div>
      <div id="radarwrap">${radarSVG(c)}</div>
    </section>
    <section class="dsec">
      <div class="sechead">Dimensions</div>
      ${DIMS.map(d=>h`
        <button class="dimrow${c.dim[d]==null?' nd':''}" data-dim="${d}">
          <span class="dn">${d}<svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></span>
          <span class="dv">${fmtv(c.dim[d])}</span>
          <span class="meter"><i style="width:0;background:${tierc(c.dim[d])}"></i></span>
        </button>`)}
    </section>
    ${(()=>{ const hl=highlights(c); const row=x=>h`<div class="hlrow"><span class="tdot" style="background:${TIER_COLORS[tierOf(x.v)]}"></span><span class="hn">${x.c}</span><span class="hltag">${x.d}</span></div>`;
      return h`<section class="dsec">
      <div class="sechead">Capability highlights</div>
      <div class="hlh brass">Key strengths</div>${hl.strengths.map(row)}
      <div class="hlh">Emerging capabilities</div>${hl.emerging.map(row)}
      <div class="hlh">Gaps &amp; opportunities</div>${hl.gaps.map(row)}
    </section>`})()}
${(()=>{ const gaps=gapsFor(c, C, {exclude:['EUU']}); if(!gaps.length) return '';
      return h`<section class="dsec">
      <div class="sechead">Where these capabilities exist</div>
      ${gaps.map(g=>h`<div class="gaprow">
        <div class="gaphead"><span class="gapname">${g.category}</span><span class="gapdim">${g.dimension}</span></div>
        ${g.holders.length
          ? h`<div class="gapwho">${g.holders.map(o=>h`<button class="pchip" data-n="${o.name}">${o.name}</button>`)}</div>`
          : h`<div class="gapnone">Nobody assessed at Established or above</div>`}
      </div>`)}
      <div class="subline" style="margin-top:12px;max-width:none">${holdersAreConcentrated(gaps)
        ? 'On the indicators collected so far these capabilities sit with the same few entities, so this says less about a match than about how sparse the assessment still is.'
        : 'Read as a statement of where a capability is assessed to exist, not as a recommendation.'}</div>
    </section>`})()}
    <div class="dfoot">Dimension = mean of its category scores · unweighted in Version 1 · <button class="mlink" data-open-method>Methodology</button><br>Source: client assessment file · Version 1, ${INDS.length} of ${META.indsV1} indicators</div>`);
  animateRadar();
  dbody.querySelectorAll('.pchip[data-n]').forEach(b=> b.onclick=()=>openCountry(byName[b.dataset.n]));
  requestAnimationFrame(()=>requestAnimationFrame(()=>{
    dbody.querySelectorAll('.dimrow').forEach(b=>{
      const dv=c.dim[b.dataset.dim];
      b.querySelector('.meter i').style.width=(dv==null?0:dv/4*100)+'%';
      b.onclick=()=>openDetail(c,b.dataset.dim);
    });
  }));
  dbody.querySelectorAll('#radar [data-dim]').forEach(el=>el.addEventListener('click',()=>openDetail(c,el.dataset.dim)));
  const ml=dbody.querySelector('[data-open-method]'); if(ml) ml.onclick=()=>openMethod();
  openDrawer();
  if(!back){
    if(c.id) setSelected(c.id);
    else if(isEU){
      /* EU: highlight member states by dimming everyone else */
      const dim=new Set(); C.forEach(x=>{ if(x.id && !EU_MEMBERS.includes(x.n)) dim.add(x.id) });
      setSelected(0); setDim(dim);
    }
    if(c.ll) flyToLL(c.ll[0], c.ll[1]-9, Math.min(cam.tDist, 2.15));
    else flyToLL(50,10,2.15);
  }
}
function openDetail(c,d){
  setView({mode:'detail', c, dim:d});
  dback.style.display='';
  dcrumb.innerHTML=String(h`${c.n.toUpperCase()} <b>›</b> ${d.toUpperCase()}`);
  const dv=c.dim[d], t=tierOf(dv);
  if(dv==null){
    dbody.innerHTML=String(h`
    <section class="dsec">
      <div class="eyebrow">${c.n} · Dimension</div>
      <h2 class="cname">${d}</h2>
      <div class="dhint" style="margin-top:16px">Not part of the current data delivery — 0 of ${COVER[d].c[1]} categories and 0 of ${COVER[d].i[1]} indicators have been collected for this dimension.</div>
    </section>
    <div class="dfoot">Excluded from the overall capability level until the data arrives · <button class="mlink" data-open-method>Methodology</button></div>`);
    const mlnd=dbody.querySelector('[data-open-method]'); if(mlnd) mlnd.onclick=()=>openMethod();
    dbody.scrollTop=0; return;
  }
  dbody.innerHTML=String(h`
    <section class="dsec">
      <div class="eyebrow">${c.n} · Dimension</div>
      <h2 class="cname">${d}</h2>
      <div class="heroline">
        <div class="hero"><span>${fmt(dv)}</span><small> / 4.00</small></div>
        <div><span class="tierchip"><span class="dot" style="background:${TIER_COLORS[t]}"></span>${TIER_NAME[t]}</span>
        <div class="subline">${COVER[d].c[0]} of ${COVER[d].c[1]} categories · ${COVER[d].i[0]} of ${COVER[d].i[1]} indicators collected</div></div>
      </div>
    </section>
    <section class="dsec">
      <div class="sechead">Categories &amp; indicators</div>
      ${catsByDim[d].map((cc,ci)=>{
        const cv=c.cat[d+'|'+cc], ct=tierOf(cv);
        const inds=INDS.map((x,i)=>({...x,i})).filter(x=>x.d===d&&x.c===cc);
        return h`<div class="cat ${ci===0?'open':''}">
          <button class="cathead">
            <svg class="chev" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg>
            <span class="cn">${cc}</span>
            <span class="cbar"><i style="width:${cv/4*100}%;background:${TIER_COLORS[ct]}"></i></span>
            <span class="cv">${fmt(cv)}</span>
          </button>
          <div class="catbody">
            ${inds.map(x=>h`<div class="ind">
                <span class="in">${x.n}</span>
                <span class="imetric">${x.m}</span>
              </div>`)}
          </div>
        </div>`})}
    </section>
    <div class="dfoot">Category score = mean of its indicator assessments · individual indicator scores are not published<br><button class="mlink" data-open-method>See indicator definitions — Methodology</button></div>`);
  dbody.querySelectorAll('.cathead').forEach(b=>b.onclick=()=>b.parentElement.classList.toggle('open'));
  const ml2=dbody.querySelector('[data-open-method]'); if(ml2) ml2.onclick=()=>openMethod();
  dbody.scrollTop=0;
}
export function openRegion(r){
  setView({mode:'region', r});
  dback.style.display='none';
  dcrumb.innerHTML=String(h`ATLAS <b>›</b> ${r.toUpperCase()}`);
  const members=C.filter(c=>c.r===r).sort((a,b)=>a.n.localeCompare(b.n));
  const avgDim={}; DIMS.forEach(d=>{ avgDim[d]= hasDim(d) ? members.reduce((a,c)=>a+c.dim[d],0)/members.length : null });
  dbody.innerHTML=String(h`
    <section class="dsec">
      <div class="eyebrow">Region</div>
      <h2 class="cname">${r}</h2>
      <div class="subline" style="margin-top:10px">${members.length} countries in the atlas · regional profile is the mean of member signatures</div>
    </section>
    <section class="dsec">
      <div class="sechead">Regional dimension signature</div>
      <div id="radarwrap">${radarSVG({dim:avgDim})}</div>
    </section>
    <section class="dsec">
      <div class="sechead">Countries</div>
      ${members.map(c=>h`
        <button class="crow crow-flat" data-n="${c.n}">
          <span class="tdot" style="background:${colorOf(c)}"></span>
          <span class="rn">${c.n}</span>
          <span class="rt">${TIER_NAME[tierOf(c.overall)]}</span>
        </button>`)}
    </section>
    <div class="dfoot">Countries listed alphabetically · colour = capability level, as on the globe</div>`);
  animateRadar();
  dbody.querySelectorAll('.crow').forEach(b=>b.onclick=()=>openCountry(byName[b.dataset.n]));
  openDrawer(); setSelected(0);
}

/* ---------- methodology ---------- */
export function openMethod(){
  setView({mode:'method'});
  dback.style.display='none';
  dcrumb.innerHTML=String(h`ATLAS <b>›</b> METHODOLOGY`);
  dbody.innerHTML=String(h`
    <section class="dsec">
      <div class="eyebrow">Methodology · Version 1</div>
      <h2 class="cname">How the atlas is built</h2>
      <div class="subline" style="max-width:440px;margin-top:12px">The atlas maps capability — it does not rank countries.
      Each indicator is assessed on a 0–4 maturity scale against published criteria; a category is the mean
      of its indicators, a dimension the mean of its categories. Results appear on the globe as four
      capability levels. Version 1 is unweighted; category weights arrive in a later release.</div>
      <div class="mtiers">${TIER_NAME.map((n,i)=>h`<span class="tierchip"><span class="dot" style="background:${TIER_COLORS[i]}"></span>${n} · ${TIER_LABEL[i]}</span>`)}</div>
    </section>
    <section class="dsec">
      <div class="sechead">Dimensions, categories &amp; indicators</div>
      ${DIMS.map((d,di)=>h`<div class="cat ${di===0?'open':''}">
        <button class="cathead">
          <svg class="chev" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg>
          <span class="cn">${d}</span>
          <span class="cv" style="grid-column:4;font-family:var(--mono);font-size:11px;color:var(--ink3)">${COVER[d].i[0]} of ${COVER[d].i[1]} ind.</span>
        </button>
        <div class="catbody">
          ${hasDim(d)?'':h`<div class="dhint">Nothing collected for this dimension yet.</div>`}
          ${catsByDim[d].map(cc=>h`
            <div class="mcat">${cc}</div>
            ${INDS.filter(x=>x.d===d&&x.c===cc).map(x=>h`<div class="ind">
              <span class="in">${x.n}</span>
              <span class="imetric">${x.m}</span>
              <span class="idesc" title="${x.t}">${x.t}</span>
            </div>`)}`)}
        </div>
      </div>`)}
    </section>
    <section class="dsec">
      <div class="sechead">Data coverage · Version 1 delivery</div>
      <div class="subline" style="max-width:none;margin-bottom:14px">The overall capability level is the mean of the dimensions that carry data. A dimension still being collected is shown as “not collected”, never as a zero.</div>
      ${DIMS.map(d=>h`<div class="ind">
        <span class="in">${d}</span>
        <span class="imetric">${COVER[d].i[0]} / ${COVER[d].i[1]} indicators</span>
        <span class="idesc">${COVER[d].c[0]} of ${COVER[d].c[1]} categories collected</span>
      </div>`)}
    </section>
    <div class="dfoot">Version 1 indicator set — ${META.indsV1} indicators, ${INDS.length} collected so far · assessments 0–4 against published criteria<br>Source: client assessment file · unweighted</div>`);
  dbody.querySelectorAll('.cathead').forEach(b=>b.onclick=()=>b.parentElement.classList.toggle('open'));
  openDrawer(); setSelected(0);
}

/* ---------- assisted discovery ---------- */
const LVL_OPTS=[['Any',0],['Developing +',1],['Established +',2],['Advanced',3]];
let disc={dim:null, cat:null, lvl:0};
export function openDiscover(dim=null, cat=null, lvl=0){
  disc={dim, cat, lvl};
  setView({mode:'discover'});
  dback.style.display='none';
  dcrumb.innerHTML=String(h`ATLAS <b>›</b> DISCOVER`);
  renderDiscover();
  openDrawer(); setSelected(0);
}
function discMetric(c){ return disc.cat ? c.cat[disc.dim+'|'+disc.cat] : c.dim[disc.dim] }
function renderDiscover(){
  const cats = disc.dim ? catsByDim[disc.dim] : [];
  let results=[];
  const nod = disc.dim && !hasDim(disc.dim);
  if(disc.dim && !nod) results = C.filter(c=> tierOf(discMetric(c))>=disc.lvl).sort((a,b)=>a.n.localeCompare(b.n));
  dbody.innerHTML=String(h`
    <section class="dsec">
      <div class="eyebrow">Assisted discovery</div>
      <h2 class="cname">Find a capability</h2>
      <div class="subline" style="margin-top:10px">Start from what you need — the atlas shows where it exists.</div>
    </section>
    <section class="dsec">
      <div class="sechead">1 · What capability are you looking for?</div>
      <div class="dgrid">${DIMS.map(d=>h`<button class="dimcard ${disc.dim===d?'on':''}${hasDim(d)?'':' nodata'}" data-d="${d}">${d}</button>`)}</div>
      ${disc.dim?h`<div class="sechead" style="margin-top:18px">Narrow to a category — optional</div>
      <div class="chiprow"><button class="fchip ${!disc.cat?'on':''}" data-cat="">All of ${disc.dim}</button>
        ${cats.map(cc=>h`<button class="fchip ${disc.cat===cc?'on':''}" data-cat="${cc}">${cc}</button>`)}</div>`:''}
    </section>
    <section class="dsec">
      <div class="sechead">2 · Minimum maturity</div>
      <div class="chiprow">${LVL_OPTS.map(([lb,v])=>h`<button class="fchip ${disc.lvl===v?'on':''}" data-lvl="${v}">${lb}</button>`)}</div>
    </section>
    <section class="dsec">
      <div class="sechead">${(disc.dim&&!nod)?h`Where it exists · ${results.length} ${results.length===1?'country':'countries'}`:'Results'}</div>
      ${nod ? h`<div class="dhint">Nothing has been collected for ${disc.dim} in this delivery.</div>`
        : disc.dim
        ? (results.length? results.map(c=>h`<button class="crow crow-flat" data-n="${c.n}">
            <span class="tdot" style="background:${TIER_COLORS[tierOf(discMetric(c))]}"></span>
            <span class="rn">${c.n}</span>
            <span class="rt">${TIER_NAME[tierOf(discMetric(c))]}</span>
          </button>`) : h`<div class="dhint">No countries at this maturity yet — try a lower threshold.</div>`)
        : h`<div class="dhint">Pick a capability above to see where it exists. The globe recolours to match.</div>`}
    </section>
    <div class="dfoot">Countries listed alphabetically · matching countries stay lit on the globe<br>Source: client assessment file · Version 1, ${INDS.length} of ${META.indsV1} indicators</div>`);
  dbody.querySelectorAll('.dimcard').forEach(b=> b.onclick=()=>{ disc.dim = disc.dim===b.dataset.d? null : b.dataset.d; disc.cat=null; renderDiscover() });
  dbody.querySelectorAll('[data-cat]').forEach(b=> b.onclick=()=>{ disc.cat = b.dataset.cat||null; renderDiscover() });
  dbody.querySelectorAll('[data-lvl]').forEach(b=> b.onclick=()=>{ disc.lvl = +b.dataset.lvl; renderDiscover() });
  dbody.querySelectorAll('.crow').forEach(b=> b.onclick=()=>openCountry(byName[b.dataset.n]));
  /* sync the globe: recolour by capability, keep only matches lit */
  mapView.colorMode = disc.dim || 'overall';
  document.querySelectorAll('#modechips .tab').forEach(x=>x.classList.toggle('on', x.dataset.m===mapView.colorMode));
  repaint(true); renderLegend(); buildBeacons();
  if(disc.dim && !nod){
    const hide=new Set();
    C.forEach(c=>{ if(c.id && tierOf(discMetric(c))<disc.lvl) hide.add(c.id) });
    setDim(hide.size?hide:null);
  } else setDim(null);
}

export function initDrawer() {
  document.getElementById('dclose').onclick = closeDrawer;
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer() });
  document.getElementById('discoverBtn').onclick = () => openDiscover();
}
